import { createContext, useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from 'src/utils/axios';
import { isValidToken, setSession } from 'src/utils/employeeJwt';
import axiosInstance from 'src/utils/axios';
import localStorageAvailable from '@utils/localStorageAvailable';
import { useRouter } from 'node_modules/next/router';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isRegistered: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { isRegistered } = action.payload;
    return {
      ...state,
      isRegistered,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'employeeJwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

EmployeeAuthProvider.propTypes = {
  children: PropTypes.node,
};

function EmployeeAuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const storageAvailable = localStorageAvailable();
  const router = useRouter();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? window.localStorage.getItem('employeeAccessToken') : '';

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        await axiosInstance.get('/employee/auth/profile').then((response) => {
          if (response.status === 200) {
            const { user } = response.data;

            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                user,
              },
            });
          }
        });
      } else {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize, router]);

  const login = async (email, password, enqueueSnackbar) => {
    const response = await axios.post('/employee/auth/signin', {
      email,
      password,
    });

    if (response.status === 200) {
      const { accessToken, user, message, companyEmployee } = response.data;
      enqueueSnackbar(message);

      setSession(accessToken);
      dispatch({
        type: 'LOGIN',
        payload: {
          user: companyEmployee || user,
        },
      });
    }
  };

  const register = async (email, username, password, firstName, midName, lastName, enqueueSnackbar) => {
    const response = await axios.post('/employee/auth/signup', {
      email,
      username,
      password,
      first_name: firstName,
      mid_name: midName,
      last_name: lastName,
      fullname: `${firstName} ${midName} ${lastName}`,
    });
    const { message } = response.data;
    enqueueSnackbar(message);

    dispatch({
      type: 'REGISTER',
      payload: {
        isRegistered: true,
      },
    });
  };

  const logout = async () => {
    await axiosInstance.delete('/employee/auth/signout').then((response) => {
      if (response.status === 200) {
        setSession(null);
        dispatch({ type: 'LOGOUT' });
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'employeeJwt',
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, EmployeeAuthProvider };
